// ---------------------------------------------------------------------------
// Data Validation Hook — real-time validation for variables/datasources/events.
//
// Wave-7 carryover: every message below was hardcoded English in a five-locale
// product, and all six reach the operator — `data-inspector.tsx` renders the
// field-scoped ones under the field rows and the entity-scoped ones in the
// panel body, `entity-tree.tsx` and `data-canvas.tsx` read the same list. A
// Korean bank operator wiring their app's data read English error text.
//
// The copy is resolved HERE rather than at the three render sites so the three
// cannot drift apart. `useStudioLocale()` makes this a locale-reactive hook;
// the memo takes `copy` as a dependency so switching language re-derives the
// messages instead of serving the previous locale's.
//
// IDENTIFIERS STAY VERBATIM inside the localized sentences — variable names,
// block names and flow names are project payload the operator matches against
// the JSON. So do `#{}` (expression syntax) and `REST` (a datasource TYPE name
// the inspector's own gate pins verbatim in the type selector).
// ---------------------------------------------------------------------------

import { useMemo } from 'react'
import { useStudioLocale } from '@/i18n'
import type { StudioDictionary } from '@/i18n'
import type { EntityIndex, VariableEntity } from './use-entity-index'

type ValidationCopy = StudioDictionary['dataValidation']['messages']

export interface ValidationMessage {
  entityId: string
  field?: string
  level: 'error' | 'warning'
  message: string
}

/** Validate #{...} expression syntax — returns a localized error or null. */
function validateExpression(expr: string, copy: ValidationCopy): string | null {
  const opens = (expr.match(/#\{/g) ?? []).length
  const closes = (expr.match(/\}/g) ?? []).length
  if (opens !== closes) return copy.unmatchedBrackets
  // Check for empty expressions
  if (/#\{\s*\}/.test(expr)) return copy.emptyExpression
  return null
}

/** Collect all #{varName} references across all flows and expressions. */
function collectReferencedVars(project: any): Set<string> {
  const refs = new Set<string>()
  const text = JSON.stringify(project ?? {})
  const matches = text.matchAll(/#\{([^}]+)\}/g)
  for (const m of matches) {
    const path = m[1].split('.')[0]
    refs.add(path)
  }
  return refs
}

export function useDataValidation(index: EntityIndex, project: any): ValidationMessage[] {
  const { strings } = useStudioLocale()
  const copy = strings.dataValidation.messages

  return useMemo(() => {
    const messages: ValidationMessage[] = []
    const varNames = new Map<string, VariableEntity[]>()
    const referencedVars = collectReferencedVars(project)

    // 1. Duplicate variable name check
    for (const v of index.variables) {
      const key = `${v.blockName}:${v.name}`
      const list = varNames.get(key) ?? []
      list.push(v)
      varNames.set(key, list)
    }
    for (const [, vars] of varNames) {
      if (vars.length > 1) {
        for (const v of vars) {
          messages.push({
            entityId: v.id,
            field: 'name',
            level: 'error',
            message: copy.duplicateVariableName(v.name, v.blockName),
          })
        }
      }
    }

    // 2. Expression syntax validation
    for (const v of index.variables) {
      if (v.expression) {
        const err = validateExpression(v.expression, copy)
        if (err) {
          messages.push({ entityId: v.id, field: 'expression', level: 'error', message: err })
        }
      }
      if (v.defaultValue && typeof v.defaultValue === 'string' && v.defaultValue.includes('#{')) {
        const err = validateExpression(v.defaultValue, copy)
        if (err) {
          messages.push({ entityId: v.id, field: 'defaultValue', level: 'error', message: err })
        }
      }
    }

    // 3. Unused variable warning
    const allVarNames = new Set(index.variables.map((v) => v.name))
    for (const v of index.variables) {
      if (!referencedVars.has(v.name)) {
        messages.push({
          entityId: v.id,
          level: 'warning',
          message: copy.variableUnreferenced(v.name),
        })
      }
    }

    // 4. Undeclared variable references in event bound flows
    for (const e of index.events) {
      if (e.boundFlow && !index.flows.some((f) => f.name === e.boundFlow)) {
        messages.push({
          entityId: e.id,
          field: 'flow',
          level: 'error',
          message: copy.flowMissing(e.boundFlow),
        })
      }
    }

    // 5. Datasource without endpoint
    for (const d of index.datasources) {
      if (d.sourceType === 'rest' && !d.config.endpoint && !d.config.url) {
        messages.push({
          entityId: d.id,
          field: 'endpoint',
          level: 'warning',
          message: copy.restDatasourceNoEndpoint,
        })
      }
    }

    return messages
  }, [index, project, copy])
}

/** Get validation messages for a specific entity. */
export function getEntityMessages(messages: ValidationMessage[], entityId: string): ValidationMessage[] {
  return messages.filter((m) => m.entityId === entityId)
}

/** Get messages for a specific field of an entity. */
export function getFieldMessages(messages: ValidationMessage[], entityId: string, field: string): ValidationMessage[] {
  return messages.filter((m) => m.entityId === entityId && m.field === field)
}
